import { useCallback, useEffect, useState } from "react";
import { CalendarClock, History, Loader2, MessageSquareOff, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProjectDetailSheet } from "@/components/ProjectDetailSheet";
import { ExportButton } from "@/components/ExportButton";
import { SavedViews } from "@/components/SavedViews";
import { SortBar } from "@/components/SortHeader";
import { sortRows, useSort, asNumber } from "@/lib/sorting";
import { api, fmtDay, fmtWhen } from "@/lib/api";
import { useFreshest } from "@/lib/freshest";
import { useAuth } from "@/context/AuthContext";

const LEVELS = [
  ["1", "Pushed once or more", "R1+"],
  ["2", "Pushed twice or more", "R2+"],
  ["3", "Pushed three times or more", "R3+"],
];

const AMBER = "hsl(36 92% 44%)";
const RED = "hsl(0 72% 45%)";

const Kpi = ({ label, value, hint, colour, testId }) => (
  <div className="bg-card px-5 py-4" data-testid={testId}>
    <div className="font-data text-2xl font-bold" style={colour ? { color: colour } : undefined}>
      {value}
    </div>
    <div className="font-data mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
      {label}
    </div>
    {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
  </div>
);

export default function Revisions() {
  const { scopedPlant, shows } = useAuth();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [plant, setPlant] = useState(scopedPlant || "all");
  const [minRevision, setMinRevision] = useState("2");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const { start, isFresh } = useFreshest();
  const { sort, on: sortBy, set: setSort } = useSort("revisions");

  const load = useCallback(() => {
    // Only the newest request may put rows on the screen.
    const ticket = start();
    api
      .get("/deepdive/revisions", {
        params: { plant: plant === "all" ? "" : plant, min_revision: minRevision, q },
      })
      .then((r) => isFresh(ticket) && setData(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, minRevision, q]);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div className="p-5 sm:p-8" data-testid="revisions-page">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-primary" />
        <div className="label-caps text-primary">Dates that have moved</div>
      </div>
      <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
        Every sub-stage date that has been pushed
      </h1>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        The first date a unit committed stays on record. Each later date is a revision — R1, R2, R3 —
        and carries the reason the updater gave. Start with R2 and beyond: those are the use cases
        that have slipped more than once.
      </p>

      <div className="mt-8 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Use cases affected"
          value={data.totals.use_cases}
          hint={`at ${LEVELS.find(([v]) => v === minRevision)?.[2] || "R1+"}`}
          testId="kpi-use-cases"
        />
        <Kpi
          label="Sub-stage dates pushed"
          value={data.totals.stage_dates}
          hint={`R1 ${data.levels.R1} · R2 ${data.levels.R2} · R3+ ${data.levels["R3+"]} in scope`}
          testId="kpi-stage-dates"
        />
        <Kpi
          label="Furthest push"
          value={data.totals.worst_days ? `${data.totals.worst_days} days` : "—"}
          colour={data.totals.worst_days > 90 ? RED : AMBER}
          hint="first committed date to the date now expected"
          testId="kpi-worst-days"
        />
        <Kpi
          label="Pushes with no reason"
          value={data.totals.reasons_missing}
          colour={data.totals.reasons_missing ? RED : undefined}
          hint="ask the unit to record why"
          testId="kpi-no-reason"
        />
      </div>

      <SavedViews
        screen="revisions"
        filters={{ plant, min_revision: minRevision === "2" ? "" : minRevision, q,
                   sort: sort.key, dir: sort.key ? sort.dir : "" }}
        onApply={(saved) => {
          setPlant(saved.plant || "all");
          setMinRevision(saved.min_revision || "2");
          setQ(saved.q || "");
          setSort({ key: saved.sort || "", dir: saved.dir === "desc" ? "desc" : "asc" });
        }}
        suggestions={[
          { name: "R3 and beyond", filters: { min_revision: "3" } },
          { name: "Every push (R1+)", filters: { min_revision: "1" } },
          ...(scopedPlant ? [{ name: `${scopedPlant} only`, filters: { plant: scopedPlant } }] : []),
        ]}
      />

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="revisions-search"
            placeholder="Search a use case, shop or owner…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11 rounded-none bg-card pl-9"
          />
        </div>
        <Select value={minRevision} onValueChange={setMinRevision}>
          <SelectTrigger className="h-11 w-[240px] rounded-none bg-card" data-testid="revisions-level">
            <SelectValue placeholder="How many times" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            {LEVELS.map(([value, label]) => (
              <SelectItem key={value} value={value} data-testid={`revisions-level-${value}`}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={plant} onValueChange={setPlant}>
          <SelectTrigger className="h-11 w-[220px] rounded-none bg-card" data-testid="revisions-plant">
            <SelectValue placeholder="Unit" />
          </SelectTrigger>
          <SelectContent className="max-h-[320px] rounded-none bg-popover">
            <SelectItem value="all">All units</SelectItem>
            {data.plants.map((p) => (
              <SelectItem key={p.code} value={p.code} data-testid={`revisions-plant-${p.code}`}>
                {p.code} — {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ExportButton
          path="/deepdive/revisions/export"
          params={{ plant: plant === "all" ? "" : plant, min_revision: minRevision, q }}
          testId="export-revisions"
          label="Download this view"
        />
      </div>

      {data.totals.by_unit.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="revisions-by-unit">
          {data.totals.by_unit.map((u) => (
            <span
              key={u.plant}
              className="font-data border border-border bg-card px-3 py-1.5 text-[11px] text-muted-foreground"
            >
              {u.plant}
              <span className="ml-2 font-bold text-foreground">{u.stage_dates}</span>
            </span>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2 border border-border bg-card px-5 py-3">
        <SortBar
          testId="revisions-sort"
          sort={sort}
          onSort={sortBy}
          columns={[
            ["code", "Code"],
            ["plant", "Unit"],
            ["title", "Project"],
            ["revision", "Times pushed"],
            ["days_pushed", "Days later"],
            ["expected_on", "New date"],
          ]}
        />
      </div>

      <div className="mt-px divide-y divide-border border border-border bg-card" data-testid="revisions-list">
        {data.rows.length === 0 && (
          <p className="px-5 py-10 text-sm text-muted-foreground" data-testid="revisions-empty">
            Nothing has been pushed this many times. Every committed date in scope is still holding.
          </p>
        )}
        {sortRows(data.rows, sort.key, sort.dir, (r, key) =>
          key === "days_pushed" ? asNumber(r.days_pushed) : r[key],
        ).map((r) => (
          <div
            key={`${r.id}-${r.stage}`}
            className="cursor-pointer px-5 py-4 transition-colors duration-200 hover:bg-muted"
            data-testid={`revision-row-${r.code}-${r.stage.replace(/\s+/g, "-")}`}
            onClick={() => setOpenId(r.id)}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-data text-[10px] uppercase tracking-widest text-muted-foreground">
                  {r.code} · {r.plant}
                  {r.is_target_value && (
                    <span className="ml-2 border border-primary px-1 text-[9px] text-primary">
                      Target value
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-sm font-semibold">{r.title}</div>
                <div className="text-[11px] text-muted-foreground">
                  {r.stage} · {r.dept_shop || "—"} · now at {r.phase || "—"} · {r.status}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-4">
                <div className="text-right">
                  <div className="font-data text-[11px] text-muted-foreground line-through">
                    {fmtDay(r.committed_on)}
                  </div>
                  <div className="font-data text-sm font-bold" style={{ color: AMBER }}>
                    {fmtDay(r.expected_on)}
                  </div>
                </div>
                <div className="text-right">
                  <span
                    className="font-display border px-2 py-1 text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: AMBER, borderColor: AMBER }}
                    data-testid={`revision-level-${r.code}`}
                  >
                    {r.revision}
                  </span>
                  {r.days_pushed !== null && r.days_pushed !== undefined && (
                    <div className="font-data mt-1 text-[10px] text-muted-foreground">
                      {r.days_pushed} day(s) later
                    </div>
                  )}
                </div>
              </div>
            </div>

            <ol className="mt-3 space-y-1.5 border-l border-border pl-4">
              {r.revisions.map((v) => (
                <li key={v.revision} className="text-[11px]">
                  <span className="font-data font-bold" style={{ color: AMBER }}>
                    {v.revision}
                  </span>
                  <span className="font-data ml-2">{fmtDay(v.date)}</span>
                  {v.days_pushed !== null && v.days_pushed !== undefined && (
                    <span className="ml-2 text-muted-foreground">(+{v.days_pushed}d)</span>
                  )}
                  <span className="ml-2 text-muted-foreground">
                    {v.by ? `· ${v.by}` : ""} {v.at ? `· ${fmtWhen(v.at)}` : ""}
                  </span>
                  <div className="mt-0.5 flex items-start gap-1.5">
                    {v.reason && shows("revisions.reasons") ? (
                      <span className="text-muted-foreground">“{v.reason}”</span>
                    ) : (
                      <span className="flex items-center gap-1.5" style={{ color: RED }}>
                        <MessageSquareOff className="h-3 w-3" /> No reason recorded
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
        <CalendarClock className="h-3.5 w-3.5" />
        Showing {data.rows.length} pushed sub-stage date(s). Open a row to read the full journey and
        the dates behind it.
      </div>

      <ProjectDetailSheet
        projectId={openId}
        open={!!openId}
        onOpenChange={(v) => !v && setOpenId(null)}
        meta={meta}
        onChanged={load}
      />
    </div>
  );
}
